/**
 * utmCapture — التقاط UTM parameters من URL وحفظها عبر السلّة إلى الطلب.
 *
 * لماذا: روابط الإعلانات تحمل ?utm_source=instagram&utm_campaign=uae_sept.
 * الدفع يحدث على Shopify checkout، فنحتاج لنقل هذه القيم من جلسة المتصفّح
 * إلى note_attributes → أعمدة utm_* في جدول orders.
 *
 * القواعد:
 * - نلتقط فقط المفاتيح المعروفة: source, medium, campaign, content, term.
 * - آخر نقرة (last-click) تحلّ محلّ السابقة.
 * - لا نخترع قيماً: إن لم يوجد UTM نترك الحقول null.
 * - referrer_domain يُستخرج من document.referrer كـfallback للمصدر.
 */

import { hasAnalyticsConsent } from "@/lib/cookie-consent";

const STORAGE_KEY = "lecmo:utm";
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // ٩٠ يوماً — نافذة تحويل الإعلانات

/** مفاتيح سمات السلّة التي يملكها هذا المصدر (تُنظَّف عند سحب الموافقة). */
export const UTM_ATTR_KEYS = [
  "_utm_source",
  "_utm_medium",
  "_utm_campaign",
  "_utm_content",
  "_utm_term",
  "_utm_captured_at",
] as const;


export interface UtmParams {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
  /** نطاق المُحيل إن لم يوجد UTM (مثلاً instagram.com) */
  referrerDomain: string | null;
  /** ISO timestamp للتقاط القيم */
  capturedAt: string;
  expires: number;
}

const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;

function clean(value: string | null): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!v || v.length > 512) return null;
  return v;
}

function referrerDomain(url: string | null): string | null {
  if (!url) return null;
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    return host || null;
  } catch {
    return null;
  }
}

/** يحذف UTM المحفوظ — يُستدعى عند سحب موافقة التحليلات. */
export function clearUtmParams(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* التخزين محجوب */
  }
}

/** يقرأ UTM المحفوظ (أو null إن انتهت الصلاحيّة أو سُحبت الموافقة). */
export function getUtmParams(): UtmParams | null {
  if (typeof window === "undefined") return null;
  if (!hasAnalyticsConsent()) {
    clearUtmParams();
    return null;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UtmParams;
    if (!parsed?.expires || parsed.expires < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * يقرأ الـURL الحاليّ ويحفظ UTM إن وُجد.
 * آخر نقرة تحلّ محلّ الأقدم (last-click).
 */
export function captureUtmParams(search?: string, referrer?: string): UtmParams | null {
  if (typeof window === "undefined") return null;
  // ممنوع الحفظ قبل موافقة التحليلات.
  if (!hasAnalyticsConsent()) {
    clearUtmParams();
    return null;
  }
  try {

    const params = new URLSearchParams(search ?? window.location.search);
    const captured: UtmParams = {
      source: clean(params.get("utm_source")),
      medium: clean(params.get("utm_medium")),
      campaign: clean(params.get("utm_campaign")),
      content: clean(params.get("utm_content")),
      term: clean(params.get("utm_term")),
      referrerDomain: referrerDomain(referrer ?? document.referrer),
      capturedAt: new Date().toISOString(),
      expires: Date.now() + TTL_MS,
    };

    const hasUtm = UTM_KEYS.some((k) => !!captured[k]);
    if (!hasUtm) {
      // لا UTM جديد: نُبقي القديم إن وُجد، وإلّا نحفظ المصدر فقط
      const existing = getUtmParams();
      if (existing) return existing;
      if (!captured.referrerDomain) return null;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
      return captured;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    return captured;
  } catch {
    return null;
  }
}

/**
 * سمات السلّة الخاصّة بـUTM (بادئة `_` = مخفيّة عن العميل في شوبيفاي).
 */
export function buildUtmAttributes(): Array<{ key: string; value: string }> {
  const utm = getUtmParams();
  if (!utm) return [];
  const attrs: Array<{ key: string; value: string }> = [];
  for (const k of UTM_KEYS) {
    const v = utm[k];
    if (v) attrs.push({ key: `_utm_${k}`, value: v });
  }
  if (utm.referrerDomain && !utm.source) {
    attrs.push({ key: `_utm_source`, value: utm.referrerDomain });
  }
  if (attrs.length > 0) attrs.push({ key: "_utm_captured_at", value: utm.capturedAt });
  return attrs;
}

/** يُستخدم في الأدمن لبناء روابط UTM جاهزة. */
export function buildUtmUrl(
  baseUrl: string,
  params: Partial<Pick<UtmParams, "source" | "medium" | "campaign" | "content" | "term">>,
): string {
  const u = new URL(baseUrl, baseUrl.startsWith("http") ? undefined : "http://x");
  for (const k of UTM_KEYS) {
    const v = params[k];
    if (v) u.searchParams.set(`utm_${k}`, v);
  }
  return u.toString().replace(/^http:\/\/x/, "");
}
